import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "./useAuth";
import { myAxios } from "../utils/common";

const NOTES_PREFIX = "kaizu_notes_";
const DEBOUNCE_MS = 1000;

export function useNotes(contextType: string, contextId: string | number) {
  const { user } = useAuth();
  const lsKey = `${NOTES_PREFIX}${user?.id}_${contextType}_${contextId}`;
  const numericContextId =
    typeof contextId === "string" ? parseInt(contextId) : contextId;

  const [note, setNote] = useState("");
  const [isSaved, setIsSaved] = useState(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  // Load: API first, localStorage fallback
  useEffect(() => {
    isMountedRef.current = true;
    const lsContent = localStorage.getItem(lsKey) || "";

    myAxios
      .get(`/notes?context_type=${contextType}&context_id=${numericContextId}`)
      .then((res) => {
        if (!isMountedRef.current) return;
        const apiContent = res.data?.content || "";
        if (apiContent) {
          setNote(apiContent);
          localStorage.setItem(lsKey, apiContent);
        } else if (lsContent) {
          setNote(lsContent);
          // Migrate localStorage note to API
          myAxios.post("/notes", {
            context_type: contextType,
            context_id: numericContextId,
            content: lsContent,
          });
        }
      })
      .catch(() => {
        if (isMountedRef.current && lsContent) setNote(lsContent);
      });

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lsKey, contextType, numericContextId]);

  // Auto-save with debounce (API + localStorage)
  const updateNote = useCallback(
    (value: string) => {
      setNote(value);
      setIsSaved(false);

      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        // localStorage as cache
        if (value.trim()) {
          localStorage.setItem(lsKey, value);
        } else {
          localStorage.removeItem(lsKey);
        }

        // Save to API
        myAxios
          .post("/notes", {
            context_type: contextType,
            context_id: numericContextId,
            content: value,
          })
          .then(() => {
            if (isMountedRef.current) setIsSaved(true);
          })
          .catch(() => {
            if (isMountedRef.current) setIsSaved(true);
          });
      }, DEBOUNCE_MS);
    },
    [lsKey, contextType, numericContextId]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return { note, updateNote, isSaved };
}

// Get all notes for a user (from API)
export function getAllNotes(
  userId: number
): Promise<
  { context_type: string; context_id: number; content: string }[]
> {
  return myAxios.get("/notes/all").then((r) => r.data);
}
